import { localeDomainMap } from "./domains";
import { brandTranslations } from "./routeTranslations";

// Map from domain locales to Lingui locales (underscore format)
const DOMAIN_TO_LINGUI_LOCALE_MAP: Record<string, string> = {
  "de-at": "de_AT",
  "en-gb": "en_GB",
  "en-us": "en_US",
  "en-dk": "en_DK",
  "en-se": "en_SE",
  "es-es": "es_ES",
  "it-it": "it_IT",
  "pl-pl": "pl_PL",
  "nl-be": "nl_BE",
  "fr-be": "fr", // assuming fr-be maps to fr
  // Base locales stay the same
  de: "de",
  en: "en",
  fr: "fr",
  nl: "nl",
};

/**
 * Convert domain locale (with hyphens) to Lingui locale (with underscores)
 * @param domainLocale - Locale from domain detection (e.g., 'en-gb')
 * @returns Lingui-compatible locale (e.g., 'en_GB')
 */
export const domainToLinguiLocale = (domainLocale: string): string => {
  return DOMAIN_TO_LINGUI_LOCALE_MAP[domainLocale] || domainLocale || "en";
};

/**
 * Get current Lingui-compatible locale
 * @returns Locale that matches your lingui.config.js
 */
export const getCurrentLinguiLocale = (): string => {
  // Try to detect locale from current URL if on client side
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const tld = hostname.split(".").pop();

    // Find locale by TLD
    const foundLocale = Object.entries(localeDomainMap).find(
      ([, domain]) => domain === tld
    )?.[0];

    if (foundLocale) {
      return domainToLinguiLocale(foundLocale);
    }
  }

  // Fallback to environment locale or default
  const envLocale = process.env.LOCALE || "nl";
  return domainToLinguiLocale(envLocale);
};

/**
 * Simple function to get the base URL for a given locale
 * Uses existing getDomainWithoutLocale logic and localeDomainMap
 *
 * @param locale - The locale (e.g., 'nl', 'de', 'en-gb')
 * @returns The complete base URL (e.g., 'https://www.otrium.nl')
 */
export const getBaseUrl = (locale = "nl"): string => {
  const isProd = process.env.RELEASE_STAGE === "production";
  const domainWithoutLocale = isProd
    ? "https://www.otrium."
    : "https://staging.otrium.";

  const tld = localeDomainMap[locale] || "com";

  return `${domainWithoutLocale}${tld}`;
};

/**
 * Get the current shop's base URL based on environment or current locale
 * @returns The base URL for the current shop
 */
export const getCurrentShopBaseUrl = (): string => {
  // Try to detect locale from current URL if on client side
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const tld = hostname.split(".").pop();

    // Find locale by TLD
    const foundLocale = Object.entries(localeDomainMap).find(
      ([, domain]) => domain === tld
    )?.[0];

    if (foundLocale) {
      return getBaseUrl(foundLocale);
    }
  }

  // Fallback to environment locale or default
  const locale = process.env.LOCALE || "nl";
  return getBaseUrl(locale);
};

/**
 * Convert a relative URL to absolute URL for current shop
 * @param relativeUrl - The relative URL (e.g., '/product/some-product')
 * @returns The absolute URL
 */
export const toAbsoluteUrl = (
  relativeUrl: string | undefined
): string | undefined => {
  if (!relativeUrl) {
    return undefined;
  }

  const baseUrl = getCurrentShopBaseUrl();
  const cleanUrl = relativeUrl.startsWith("/")
    ? relativeUrl
    : `/${relativeUrl}`;
  return `${baseUrl}${cleanUrl}`;
};

/**
 * Get the path by type and gender
 * @param type - The type of the path (e.g., 'brand', 'category')
 * @param gender - The gender of the path (e.g., 'men', 'women')
 * @param slug - The slug of the path (e.g., 'some-brand')
 * @param locale - The locale of the path (e.g., 'en', 'fr')
 * @returns The path by type and gender
 */
export const getPathByTypeAndGender = ({
  type,
  gender,
  slug,
  locale,
}: {
  type: string;
  gender?: string;
  slug: string;
  locale: string;
}): string | undefined => {
  if (type === "brand") {
    const path = `/${
      brandTranslations[locale as keyof typeof brandTranslations]
    }/${slug}`;

    return gender ? `${path}/${gender}` : path;
  }

  if (type === "category") {
    return gender ? `/${gender}/${slug}` : `/${slug}`;
  }

  return undefined;
};

/**
 * Get all brand paths
 * @param locale - The locale of the path (e.g., 'en', 'fr')
 * @returns The all brand paths
 */
export const getAllBrandPath = (locale: string): string => {
  return brandTranslations[locale as keyof typeof brandTranslations];
};
